// src/views/Menu.jsx
import React, {
    Suspense,
    useState,
    useEffect,
    useContext,
    createContext,
    useMemo,
    useCallback,
    useRef,
    lazy,
} from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@sentry/react';
import ErrorComponent from '../ErrorComponent';
import LoadingSpinner from '../components/LoadingSpinner';
import SingleProduct from './SingleProduct.jsx';

// Contexts
import { useConfig } from "../contexts/ConfigContext";
import { useAppState } from '../contexts/AppStateContext';
import { useCart } from "../contexts/CartContext";
import { FilterProvider, useFilter } from '../contexts/FilterContext';

// Custom Hooks
import useWindowSize from '../hooks/useWindowSize';
import { useMenuProducts } from '../hooks/menu/useMenuProducts';
import { useMenuSpecials } from '../hooks/menu/useMenuSpecials';
import { useInfiniteScroll } from '../hooks/menu/useInfiniteScroll';
import { useMenuNavigation } from '../hooks/menu/useMenuNavigation';
import { useMenuSEO } from '../hooks/menu/useMenuSEO';
import { useDirectNavigation } from '../hooks/menu/useDirectNavigation';

// Utilities
import { getOrderText, consoleDebug, getPrioritySpecialForProduct } from '../util';
import { defaultSort } from '../config';
import { canDisplayMoreProducts } from '../utils/menu/productUtils';
import { countActiveFilters } from '../utils/menu/filterUtils';
import { scrollToSpecials } from '../utils/scrollUtils';

// Direct Imports for Core Components (no lazy loading to prevent spinner flashes)
import FilterNavbar from '../components/Filter/FilterNavbar';
import ProductNav from '../components/Menu/ProductNav';
import Products from '../components/Products/Products';
import Filter from '../components/Filter/Filter';
import MobileSpecials from '../components/Menu/MobileSpecials';
import Specials from '../components/Products/Specials';
import MenuBanners from '../components/Menu/MenuBanners';

// Lazy Loaded Components (only for truly optional/rare components)
const StoreNotice = lazy(() => import('../components/Menu/StoreNotice.jsx'));
const Surfside = lazy(() => import('../components/Surfside/Surfside'));

// Context Creation
export const PriceRangeContext = createContext(null);
export const QueryArgsContext = createContext(null);


// Isolated Specials Container Component
const SpecialsContainer = ({ specialsList, selectedSpecial, setSelectedSpecialId }) => {
    useEffect(() => {
        consoleDebug('🎪 SpecialsContainer rendering', {
            hasSpecialsList: Boolean(specialsList?.length),
            specialsCount: specialsList?.length || 0,
            hasSelectedSpecial: Boolean(selectedSpecial),
            selectedSpecialId: selectedSpecial?.id,
            displayMode: selectedSpecial ? 'single-special' : 'specials-collection'
        });
    }, [specialsList, selectedSpecial]);

    if (!specialsList || !specialsList.length) {
        consoleDebug('⚠️ SpecialsContainer has no specials to display');
        return null;
    }

    return (
        <ErrorBoundary
            fallback={(props) =>
                <ErrorComponent
                    {...props}
                    componentName="Specials"
                    parentName="Menu"
                />
            }
        >
            <Specials
                selectedSpecial={selectedSpecial}
                specials={specialsList}
                setSelectedSpecialId={setSelectedSpecialId}
            />
        </ErrorBoundary>
    );
};

// Main Menu Component - Just sets up the provider
const Menu = ({ rootId, forcedOrderType }) => {
    const {
        menuFilter,
        filterTerms: globalFilterTerms,
        retailers,
        tagSlugs,
        menuConfig = {}
    } = useConfig();

    const { retailerId } = useAppState();

    const retailerData = retailers?.[retailerId] || {};
    const { brandTerms = [] } = retailerData;

    // Combine filter terms with brand terms
    const filterTerms = useMemo(() => ({
        categories: [],
        subcategories: [],
        brands: [],
        effects: [],
        strains: [],
        weights: [],
        tags: [],
        ...globalFilterTerms,
        brands: brandTerms || []
    }), [globalFilterTerms, brandTerms]);

    // Get specials data
    const {
        specialsExist,
        filteredSpecialsList,
    } = useMenuSpecials(retailerId, menuConfig.showEmptySpecials);

    return (
        <ErrorBoundary
            fallback={(props) =>
                <ErrorComponent
                    {...props}
                    componentName="MenuContent"
                    parentName="Menu"
                />
            }
        >
            <FilterProvider
                menuFilter={menuFilter}
                filterTerms={filterTerms}
                retailerId={retailerId}
                specialsExist={specialsExist}
                filteredSpecialsList={filteredSpecialsList}
                tagSlugs={tagSlugs}
            >
                <MenuContent rootId={rootId} forcedOrderType={forcedOrderType} />
            </FilterProvider>
        </ErrorBoundary>
    );
};

// MenuContent - Contains all the menu logic and can use filter context
const MenuContentInner = ({ rootId, forcedOrderType }) => {
    // DEBUG: Track component mount
    useEffect(() => {
        consoleDebug('🏗️ MENU DEBUG: MenuContentInner MOUNTED', {
            timestamp: new Date().toISOString(),
            rootId,
            forcedOrderType
        });

        return () => {
            consoleDebug('💀 MENU DEBUG: MenuContentInner UNMOUNTED', {
                timestamp: new Date().toISOString()
            });
        };
    }, []);

    // Ref for scrolling to specials container when special is selected
    const specialsContainerRef = useRef(null);

    // Function to scroll to specials container when special is selected
    const scrollToSpecialsContainer = useCallback(() => {
        scrollToSpecials(specialsContainerRef);
    }, []);
    // Get filter context values
    const {
        selectedFilters,
        setSelectedFilters,
        selectedSpecialId,
        setSelectedSpecialId,
        filterTerms
    } = useFilter();

    // Core Hooks & Context
    const params = useParams();
    const location = useLocation();
    const { isMobile } = useWindowSize();

    const {
        styleOptions,
        surfside,
        analytics,
        menuConfig = {},
        menuFilter,
        retailers,
        tagSlugs,
    } = useConfig();

    const { retailerId, productPopupData, setProductPopupData } = useAppState();

    // State
    const [sort, setSort] = useState(defaultSort);
    const [isWideScreen, setIsWideScreen] = useState(
        typeof window !== 'undefined' ? window.innerWidth > 575 : true
    );
    const [activeBannerType, setActiveBannerType] = useState('menu');

    const {
        paginationLimit = 20,
        productDisplayStyle,
        numColumns,
        menuCategoriesToShow,
        hideCategoryFilter,
        hideSubcategoryFilter,
        disableMobileInfiniteScroll,
        persistMenuBanners,
        hideTagsFilter,
        showDeliveryPickup,
        showEmptySpecials,
        mobileFilterButtons,
    } = menuConfig;

    const initialDisplayStyleFromConfig = useMemo(() => {
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

        // For mobile, the "initial" style should be 'list'
        if (isMobile) {
            return 'list';
        }

        // For desktop, use the configured style
        return menuConfig?.productDisplayStyle || 'grid';
    }, [menuConfig?.productDisplayStyle]);

    const [displayStyle, setDisplayStyle] = useState(() => {
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

        // On mobile, always default to 'list'
        if (isMobile) {
            return 'list';
        }

        // On desktop, use the configured style
        return menuConfig?.productDisplayStyle || 'grid';
    });

    // Retailer Data
    const retailerData = useMemo(() =>
        (retailers && retailerId && retailers[retailerId] ? retailers[retailerId] : {}),
        [retailers, retailerId]
    );

    const {
        showMenuBannerSurfside = false,
        showMenuBanner = false,
        menuBanners = [],
        menuSlug = '',
        seoGeos = '',
        retailerName = '',
        niceName = '',
        brandTerms = [],
        showStoreNotice = false,
        storeNoticeContent = ''
    } = retailerData;

    // Search params
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get('search');
    const thcMin = searchParams.get('thcMin');

    // Data Fetching
    const { cartData, isFetched: isFetchedCartData } = useCart();

    // DEBUG: Track cart data loading
    useEffect(() => {
        consoleDebug('🛒 MENU DEBUG: Cart Data State Changed', {
            timestamp: new Date().toISOString(),
            isFetchedCartData,
            hasCartData: !!cartData,
            pricingType: cartData?.pricingType
        });
    }, [isFetchedCartData, cartData]);

    // Get specials data from the hook
    const {
        specialBrands,
        apiSpecialsList,
        specialsExist,
        filteredSpecialsList,
        filteredSpecialProductsIds,
        getSelectedSpecial,
        getMostRecentSpecialId,
        isFetchedMenuData,
        productSpecialMap,
        // Promise-based data readiness
        isDataReady,
        waitForSpecialsData,
    } = useMenuSpecials(retailerId, showEmptySpecials);

    const selectedSpecial = useMemo(() =>
        getSelectedSpecial(selectedSpecialId),
        [selectedSpecialId, getSelectedSpecial]
    );

    // DEBUG: Track specials data loading
    useEffect(() => {
        consoleDebug('🎪 MENU DEBUG: Specials Data State Changed', {
            timestamp: new Date().toISOString(),
            isFetchedMenuData,
            specialsExist,
            filteredSpecialsListExists: filteredSpecialsList !== null && filteredSpecialsList !== undefined,
            filteredSpecialsListLength: filteredSpecialsList?.length || 0,
            isDataReady,
            hasSelectedSpecial: !!selectedSpecial,
            selectedSpecialId
        });
    }, [isFetchedMenuData, specialsExist, filteredSpecialsList, isDataReady, selectedSpecial, selectedSpecialId]);

    // NOW define menuProductsParams - but notice queryArgs is not included here
    const menuProductsParams = useMemo(() => ({
        retailerId,
        selectedFilters,
        sort,
        forcedOrderType,
        cartData: cartData || { pricingType: 'RECREATIONAL' },
        menuFilter,
        tagSlugs,
        paginationLimit,
        specialsExist,
        filteredSpecialsList,
        selectedSpecialId,
        params,
        // Promise-based specials data readiness
        waitForSpecialsData,
        isDataReady
        // Note: queryArgs is NOT included in menuProductsParams
    }), [
        retailerId,
        JSON.stringify(selectedFilters),
        JSON.stringify(sort),
        forcedOrderType,
        cartData?.pricingType,
        JSON.stringify(menuFilter),
        JSON.stringify(tagSlugs),
        paginationLimit,
        specialsExist,
        JSON.stringify(filteredSpecialsList),
        selectedSpecialId,
        JSON.stringify(params),
        isDataReady
    ]);

    // THEN call useMenuProducts
    const {
        queryArgs,  // This is where queryArgs is defined
        setQueryArgs,
        processedProducts,
        totalUniqueItemsFetched,
        fetchNextPageProductsData,
        hasNextPageProductsData,
        isLoadingProducts,
        isFetchingNextPageProductsData,
        productsError,
        refetchProductsData,
        displayFactorRef,
        setDisplayFactorRef,
        displaySpecialsList,
        setDisplaySpecialsList,
        priceRange,
        setPriceRange,
    } = useMenuProducts(menuProductsParams);

    const [displayProducts, setDisplayProducts] = useState([]);
    const [currentQueryId, setCurrentQueryId] = useState(null);
    const [canShowNoProducts, setCanShowNoProducts] = useState(false);
    const [previousSelectedSpecialId, setPreviousSelectedSpecialId] = useState(selectedSpecialId);

    // DEBUG: Track queryArgs changes
    useEffect(() => {
        consoleDebug('🔍 MENU DEBUG: QueryArgs State Changed', {
            timestamp: new Date().toISOString(),
            hasQueryArgs: queryArgs !== null,
            queryName: queryArgs?.queryName,
            queryFilters: queryArgs?.variables?.menuFilter,
            sortDir: queryArgs?.variables?.sortDir,
            sortKey: queryArgs?.variables?.sortKey
        });
    }, [queryArgs]);

    // DEBUG: Track products loading states
    useEffect(() => {
        consoleDebug('📦 MENU DEBUG: Products Loading State Changed', {
            timestamp: new Date().toISOString(),
            isLoadingProducts,
            isFetchingNextPageProductsData,
            processedProductsLength: processedProducts.length,
            displayProductsLength: displayProducts.length,
            hasNextPageProductsData,
            totalUniqueItemsFetched
        });
    }, [isLoadingProducts, isFetchingNextPageProductsData, processedProducts.length, displayProducts.length, hasNextPageProductsData, totalUniqueItemsFetched]);

    // Create a stable query ID
    const latestQueryId = useMemo(() => {
        if (!queryArgs) return null;
        return JSON.stringify({
            queryName: queryArgs.queryName,
            filters: queryArgs.variables?.menuFilter,
            sort: { sortDir: queryArgs.variables?.sortDir, sortKey: queryArgs.variables?.sortKey }
        });
    }, [queryArgs]);

    // Consolidated effect for managing display products and loading states
    useEffect(() => {
        consoleDebug('🔄 MENU DEBUG: Display products effect triggered', {
            timestamp: new Date().toISOString(),
            isLoadingProducts,
            isFetchingNextPageProductsData,
            processedProductsLength: processedProducts.length,
            latestQueryId,
            currentQueryId,
            selectedSpecialId
        });

        // If loading data, don't update display (keep showing current products)
        if (isLoadingProducts || isFetchingNextPageProductsData) {
            consoleDebug('🔄 MENU DEBUG: Loading products, keeping current display', {
                timestamp: new Date().toISOString(),
                isLoadingProducts,
                isFetchingNextPageProductsData
            });
            setCanShowNoProducts(false);
            return;
        }

        // Data is ready - update everything together
        consoleDebug('🔄 MENU DEBUG: Data ready, updating display products', {
            timestamp: new Date().toISOString(),
            processedProductsLength: processedProducts.length,
            latestQueryId,
            selectedSpecialId
        });
        setDisplayProducts(processedProducts);
        setCurrentQueryId(latestQueryId);
        setPreviousSelectedSpecialId(selectedSpecialId);

        // Determine if we should show "no products" message
        const shouldShowNoProductsMessage =
            processedProducts.length === 0 &&
            currentQueryId === latestQueryId &&
            queryArgs !== null &&
            isFetchedCartData &&
            (!selectedFilters.specials || isDataReady);

        consoleDebug('🔄 MENU DEBUG: Setting canShowNoProducts', {
            timestamp: new Date().toISOString(),
            shouldShowNoProductsMessage,
            processedProductsLength: processedProducts.length,
            currentQueryId,
            latestQueryId,
            hasQueryArgs: queryArgs !== null,
            isFetchedCartData,
            selectedFiltersSpecials: selectedFilters.specials,
            isDataReady
        });

        setCanShowNoProducts(shouldShowNoProductsMessage);
    }, [
        isLoadingProducts,
        isFetchingNextPageProductsData,
        processedProducts,
        latestQueryId,
        selectedSpecialId,
        queryArgs,
        isFetchedCartData,
        selectedFilters.specials,
        isDataReady
    ]);

    // Single source of truth: Is the entire menu ready to display?
    const isMenuReady = useMemo(() => {
        const checks = {
            isFetchedCartData,
            isFetchedMenuData,
            hasQueryArgs: queryArgs !== null,
            isDataReady: !selectedFilters.specials || isDataReady,
            notInInitialLoading: !(isLoadingProducts && displayProducts.length === 0),
            displayProductsLength: displayProducts.length,
            canShowNoProducts,
            processedProductsLength: processedProducts.length,
            specialsExist,
            hasFilteredSpecialsList: filteredSpecialsList !== null && filteredSpecialsList !== undefined,
        };

        consoleDebug('🔬 MENU DEBUG: isMenuReady calculation started', {
            timestamp: new Date().toISOString(),
            allChecks: checks
        });

        // Must have cart data (needed for pricing)
        if (!isFetchedCartData) {
            consoleDebug('❌ MENU DEBUG: isMenuReady = FALSE - Missing cart data', {
                timestamp: new Date().toISOString(),
                isFetchedCartData,
                failedCheck: 'isFetchedCartData'
            });
            return false;
        }

        // Must have menu data (needed for specials)
        if (!isFetchedMenuData) {
            consoleDebug('❌ MENU DEBUG: isMenuReady = FALSE - Missing menu data', {
                timestamp: new Date().toISOString(),
                isFetchedMenuData,
                failedCheck: 'isFetchedMenuData'
            });
            return false;
        }

        // Must have specials list loaded (even if empty) to avoid CLS from late-loading specials components
        if (filteredSpecialsList === null || filteredSpecialsList === undefined) {
            consoleDebug('❌ MENU DEBUG: isMenuReady = FALSE - filteredSpecialsList not loaded', {
                timestamp: new Date().toISOString(),
                filteredSpecialsList,
                filteredSpecialsListType: typeof filteredSpecialsList,
                failedCheck: 'filteredSpecialsList'
            });
            return false;
        }

        // Must have query built
        if (queryArgs === null) {
            consoleDebug('❌ MENU DEBUG: isMenuReady = FALSE - queryArgs is null', {
                timestamp: new Date().toISOString(),
                queryArgs,
                failedCheck: 'queryArgs'
            });
            return false;
        }

        // If we're in specials mode, must wait for promise resolution
        if (selectedFilters.specials && !isDataReady) {
            consoleDebug('❌ MENU DEBUG: isMenuReady = FALSE - Waiting for specials data', {
                timestamp: new Date().toISOString(),
                selectedFiltersSpecials: selectedFilters.specials,
                isDataReady,
                failedCheck: 'isDataReady (specials mode)'
            });
            return false;
        }

        // Must not be in initial loading state
        if (isLoadingProducts && displayProducts.length === 0) {
            consoleDebug('❌ MENU DEBUG: isMenuReady = FALSE - Initial loading state', {
                timestamp: new Date().toISOString(),
                isLoadingProducts,
                displayProductsLength: displayProducts.length,
                failedCheck: 'notInInitialLoading'
            });
            return false;
        }

        // Must have either products to display OR confirmed no products
        const hasProductsOrConfirmedEmpty =
            displayProducts.length > 0 ||
            (canShowNoProducts && processedProducts.length === 0);

        if (!hasProductsOrConfirmedEmpty) {
            consoleDebug('❌ MENU DEBUG: isMenuReady = FALSE - No products to display and not confirmed empty', {
                timestamp: new Date().toISOString(),
                displayProductsLength: displayProducts.length,
                canShowNoProducts,
                processedProductsLength: processedProducts.length,
                hasProductsOrConfirmedEmpty,
                failedCheck: 'hasProductsOrConfirmedEmpty'
            });
            return false;
        }

        // All checks passed - menu is ready!
        consoleDebug('✅ MENU DEBUG: isMenuReady = TRUE - All checks passed!', {
            timestamp: new Date().toISOString(),
            allChecks: checks,
            finalState: {
                displayProductsLength: displayProducts.length,
                processedProductsLength: processedProducts.length,
                canShowNoProducts,
                hasProductsOrConfirmedEmpty
            }
        });
        return true;
    }, [
        isFetchedCartData,
        isFetchedMenuData,
        queryArgs,
        selectedFilters.specials,
        isDataReady,
        isLoadingProducts,
        displayProducts.length,
        canShowNoProducts,
        processedProducts.length,
        specialsExist,
        filteredSpecialsList
    ]);

    // Simplified loading logic - show loading when menu is not ready
    const shouldShowLoading = useMemo(() => {
        // Show loading ONLY when menu isn't ready
        // Once isMenuReady becomes true, we have all data and should display
        const showLoading = !isMenuReady;

        consoleDebug('⏳ MENU DEBUG: shouldShowLoading calculated', {
            timestamp: new Date().toISOString(),
            shouldShowLoading: showLoading,
            isMenuReady,
            displayProductsLength: displayProducts.length
        });

        return showLoading;
    }, [isMenuReady, displayProducts.length]);

    // Separate loading logic for products area (when filters change after initial render)
    const shouldShowProductsLoading = useMemo(() => {
        // Show loading spinner when:
        // 1. Products are actively loading (but not pagination)
        // 2. OR we have a query mismatch (displaying products from old query while new one loads)
        const isActivelyLoading = isLoadingProducts && !isFetchingNextPageProductsData;
        const hasQueryMismatch = currentQueryId !== latestQueryId && latestQueryId !== null;

        const showProductsLoading = isActivelyLoading || hasQueryMismatch;

        consoleDebug('⏳ MENU DEBUG: shouldShowProductsLoading calculated', {
            timestamp: new Date().toISOString(),
            shouldShowProductsLoading: showProductsLoading,
            isLoadingProducts,
            isFetchingNextPageProductsData,
            isActivelyLoading,
            hasQueryMismatch,
            currentQueryId: currentQueryId ? 'set' : 'null',
            latestQueryId: latestQueryId ? 'set' : 'null',
            idsMatch: currentQueryId === latestQueryId
        });

        return showProductsLoading;
    }, [isLoadingProducts, isFetchingNextPageProductsData, currentQueryId, latestQueryId]);

    const shouldShowNoProducts = useMemo(() => {
        // Show "no products" only when we're certain there are no results
        return canShowNoProducts &&
            processedProducts.length === 0 &&
            displayProducts.length === 0 &&
            currentQueryId === latestQueryId &&
            (!selectedFilters.specials || isDataReady) &&
            isFetchedCartData &&
            !isLoadingProducts &&
            queryArgs !== null &&
            (totalUniqueItemsFetched > 0 ||
                (selectedFilters.search && selectedFilters.search.length > 0));
    }, [
        canShowNoProducts,
        processedProducts.length,
        displayProducts.length,
        currentQueryId,
        latestQueryId,
        selectedFilters.specials,
        isDataReady,
        isFetchedCartData,
        isLoadingProducts,
        queryArgs,
        totalUniqueItemsFetched,
        selectedFilters.search
    ]);

    const {
        loadMoreButtonRef,
        handleLoadMore
    } = useInfiniteScroll({
        isWideScreen,
        disableMobileInfiniteScroll,
        isFetchingNextPage: isFetchingNextPageProductsData,
        totalUniqueItemsFetched,
        paginationLimit,
        hasNextPage: hasNextPageProductsData,
        fetchNextPage: fetchNextPageProductsData,
        displayFactorRef,
        setDisplayFactorRef
    });

    // Other State Management
    const orderText = useMemo(() =>
        getOrderText(selectedFilters, filterTerms),
        [selectedFilters, filterTerms]
    );

    const stableRefetchProductsData = useCallback(() => {
        if (queryArgs) refetchProductsData();
    }, [queryArgs, refetchProductsData]);

    useMenuNavigation({
        selectedFilters,
        setSelectedFilters,
        menuFilter,
        filterTerms,
        specialBrands,
        selectedSpecialId,
        searchQuery,
        thcMin
    });

    useMenuSEO({
        selectedFilters,
        filterTerms,
        retailerName,
        seoGeos
    });

    useDirectNavigation({
        apiSpecialsList,
        isFetchedMenuData,
        isFetchedCartData,
        menuFilter,
        retailerId,
        sort,
        cartData,
        tagSlugs,
        filterTerms,
        setSelectedSpecialId,
        setSelectedFilters,
        setQueryArgs,
        setDisplaySpecialsList,
        stableRefetchProductsData
    });

    // Memoize contexts
    const priceRangeContextValue = useMemo(
        () => ({ priceRange, setPriceRange }),
        [priceRange]
    );

    const queryArgsContextValue = useMemo(
        () => queryArgs,
        [queryArgs]
    );

    // Effects
    useEffect(() => {
        if (initialDisplayStyleFromConfig) {
            setDisplayStyle(initialDisplayStyleFromConfig);
        }
    }, [initialDisplayStyleFromConfig]);

    useEffect(() => {
        const handleResize = () => {
            setIsWideScreen(window.innerWidth > 575);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (params?.specialCategoryName) {
            setDisplaySpecialsList(false);
        } else if (selectedFilters.specials) {
            setDisplaySpecialsList(specialsExist);
        }
    }, [params, selectedFilters.specials, specialsExist, setDisplaySpecialsList]);

    useEffect(() => {
        const isDirectSpecialsNavigation = params?.categoryType === 'specials' &&
            !queryArgs && apiSpecialsList?.length > 0 && !selectedFilters.specials;

        if (isDirectSpecialsNavigation) {
            setSelectedFilters(currentFilters => ({
                ...menuFilter,
                ...currentFilters,
                specials: true
            }));
        }
    }, [params?.categoryType, queryArgs, apiSpecialsList, selectedFilters.specials, menuFilter, setSelectedFilters]);

    useEffect(() => {
        if (selectedFilters.category && surfside?.enable && surfside.showCategoryBanner) {
            setActiveBannerType('category');
        } else {
            setActiveBannerType('menu');
        }
    }, [selectedFilters.category, surfside]);

    // Early return: Show loading spinner if menu is not ready
    // This prevents Filter, ProductNav, and other components from mounting
    // and causing layout shifts before all data is loaded
    if (shouldShowLoading) {
        consoleDebug('🔴 MENU DEBUG: EARLY RETURN - Showing loading spinner', {
            timestamp: new Date().toISOString(),
            shouldShowLoading,
            isMenuReady,
            fullState: {
                isFetchedCartData,
                isFetchedMenuData,
                hasQueryArgs: queryArgs !== null,
                hasFilteredSpecialsList: filteredSpecialsList !== null && filteredSpecialsList !== undefined,
                isDataReady,
                isLoadingProducts,
                displayProductsLength: displayProducts.length,
                canShowNoProducts,
                processedProductsLength: processedProducts.length
            }
        });
        return <LoadingSpinner loadingType="menuList" />;
    }

    // If we reach here, menu is ready to display
    consoleDebug('🟢 MENU DEBUG: MENU RENDERING - Early return bypassed, rendering full menu', {
        timestamp: new Date().toISOString(),
        shouldShowLoading,
        isMenuReady,
        displayProductsLength: displayProducts.length
    });

    // Render
    return (
        <>
            <PriceRangeContext.Provider value={priceRangeContextValue}>
                <ErrorBoundary
                    fallback={(props) =>
                        <ErrorComponent
                            {...props}
                            componentName="FilterNavbar"
                            parentName="Menu"
                        />
                    }
                >
                    <FilterNavbar
                        forcedOrderType={forcedOrderType}
                        categoriesToShow={menuCategoriesToShow}
                        mobileFilterButtons={mobileFilterButtons}
                    />
                </ErrorBoundary>

                {specialsExist && !selectedFilters.specials && (
                    <ErrorBoundary
                        fallback={(props) =>
                            <ErrorComponent
                                {...props}
                                componentName="MobileSpecials"
                                parentName="Menu"
                            />
                        }
                    >
                        <MobileSpecials
                            specialsList={filteredSpecialsList}
                            setSelectedSpecialId={(id) => {
                                setSelectedSpecialId(id);
                                setSelectedFilters(sf => ({ ...menuFilter, ...sf, specials: true }));
                            }}
                            defaultDesktopSpecials={menuConfig.defaultDesktopSpecials}
                            desktopSpecialsTitle={menuConfig.desktopSpecialsTitle}
                        />
                    </ErrorBoundary>
                )}

                {showMenuBanner && (() => {
                    const hideCat = selectedFilters?.category &&
                        (!persistMenuBanners || !persistMenuBanners.includes('categories'));
                    const hideBrand = selectedFilters?.brandIds?.length > 0;
                    const hideSpecial = selectedFilters?.specials &&
                        (!persistMenuBanners || !persistMenuBanners.includes('specials'));
                    return !hideCat && !hideBrand && !hideSpecial;
                })() && (
                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="MenuBanners"
                                    parentName="Menu"
                                />
                            }
                        >
                            <MenuBanners
                                banners={menuBanners}
                                rootId={rootId}
                                setSelectedFilters={setSelectedFilters}
                                surfsideBanner={showMenuBannerSurfside}
                                surfside={surfside}
                                styleOptions={styleOptions}
                                hideSubcategoryFilter={hideSubcategoryFilter}
                                hideCategoryFilter={hideCategoryFilter}
                                hideTagsFilter={hideTagsFilter}
                                specialsExist={specialsExist}
                            />
                        </ErrorBoundary>
                    )}

                {showStoreNotice && <StoreNotice content={storeNoticeContent} />}

                <div
                    className={`${styleOptions?.primaryMenuClasses ?
                        'headless-shop-container headless-menu ' + styleOptions.primaryMenuClasses :
                        'headless-shop-container headless-menu'}`}
                    id="shop"
                >
                    {filterTerms && selectedFilters && (
                        <QueryArgsContext.Provider value={queryArgsContextValue}>
                            <ErrorBoundary
                                fallback={(props) =>
                                    <ErrorComponent
                                        {...props}
                                        componentName="Filter"
                                        parentName="Menu"
                                    />
                                }
                            >
                                <Filter
                                    categoriesToShow={menuCategoriesToShow}
                                    hideCategoryFilter={hideCategoryFilter}
                                    hideSubcategoryFilter={hideSubcategoryFilter}
                                    hideTagsFilter={hideTagsFilter}
                                    showDeliveryPickup={showDeliveryPickup}
                                    forcedOrderType={forcedOrderType}
                                />
                            </ErrorBoundary>
                        </QueryArgsContext.Provider>
                    )}

                    <div className="headless-products-container has-filter">
                        {surfside?.enable && (
                            <Suspense fallback={null}>
                                {selectedFilters?.category && surfside?.showCategoryBanner && (
                                    <div key={`cat-banner-${selectedFilters.category}`} className="surfside-banner-container">
                                        <Surfside
                                            placement="category-banner"
                                            accountId={surfside.account_id || ''}
                                            sourceId={surfside.source_id || ''}
                                            placementId={surfside.placement_ids?.menu_banner || ''}
                                            category={selectedFilters.category || ''}
                                        />
                                    </div>
                                )}
                                {!selectedFilters?.category && surfside?.showMenuBanner && !showMenuBannerSurfside && (
                                    <div key="menu-banner" className="surfside-banner-container">
                                        <Surfside
                                            placement="menu-banner"
                                            accountId={surfside.account_id || ''}
                                            sourceId={surfside.source_id || ''}
                                            placementId={surfside.placement_ids?.menu_banner || ''}
                                        />
                                    </div>
                                )}
                            </Suspense>
                        )}

                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="ProductNav"
                                    parentName="Menu"
                                />
                            }
                        >
                            <ProductNav
                                selectedFilters={selectedFilters}
                                filterTerms={filterTerms}
                                sort={sort}
                                setSort={setSort}
                                display={displayStyle}
                                setDisplayStyle={setDisplayStyle}
                                initialDisplayStyle={initialDisplayStyleFromConfig || 'card'}
                                searchQuery={searchQuery}
                            />
                        </ErrorBoundary>

                        {displaySpecialsList && filteredSpecialsList?.length > 0 && (
                            <div ref={specialsContainerRef}>
                                <SpecialsContainer
                                    specialsList={filteredSpecialsList}
                                    selectedSpecial={selectedSpecial}
                                    setSelectedSpecialId={(id) => {
                                        setSelectedSpecialId(id);
                                        // Scroll to specials container for both single special selection AND returning to all specials
                                        // Add a small delay to ensure the DOM updates before scrolling
                                        setTimeout(scrollToSpecialsContainer, 100);
                                    }}
                                />
                            </div>
                        )}

                        {(() => {
                            consoleDebug('🎨 MENU DEBUG: Products area render decision', {
                                timestamp: new Date().toISOString(),
                                shouldShowProductsLoading,
                                willRenderSpinner: shouldShowProductsLoading,
                                willRenderProducts: !shouldShowProductsLoading,
                                displayProductsLength: displayProducts.length,
                                isLoadingProducts,
                                hasQueryMismatch: currentQueryId !== latestQueryId
                            });

                            if (shouldShowProductsLoading) {
                                consoleDebug('🔄 MENU DEBUG: Rendering LoadingSpinner in products area');
                                return <LoadingSpinner loadingType="menuList" />;
                            }

                            return (
                                <>
                                    <div className={`${displayStyle === 'list' ?
                                        'headless-products-list' :
                                        `headless-products-row headless-columns-${numColumns}`}`}>
                                        {productsError ? (
                                    <ErrorComponent
                                        error={productsError}
                                        componentName="ProductsLoader"
                                        parentName="Menu"
                                        queryArgs={queryArgs}
                                        selectedFilters={selectedFilters}
                                        retailerId={retailerId}
                                        refetchFunction={stableRefetchProductsData}
                                        displayProductsCount={displayProducts?.length || 0}
                                        isLoadingProducts={isLoadingProducts}
                                        isFetchingNextPage={isFetchingNextPageProductsData}
                                        resetError={() => {
                                            // Custom reset logic for products
                                            if (stableRefetchProductsData) {
                                                stableRefetchProductsData();
                                            }
                                        }}
                                    />
                                ) : displayProducts.length > 0 ? (
                                    <ErrorBoundary
                                        fallback={(props) =>
                                            <ErrorComponent
                                                {...props}
                                                componentName="Products"
                                                parentName="Menu"
                                            />
                                        }
                                    >
                                        <Products
                                            products={displayProducts}
                                            display={displayStyle}
                                            initialDisplayStyle={initialDisplayStyleFromConfig || 'card'}
                                            selectedFilters={selectedFilters}
                                            setSelectedFilters={setSelectedFilters}
                                            productSpecialMap={productSpecialMap}
                                            specialsExist={specialsExist}
                                            specialsList={apiSpecialsList}
                                            specialProductsIds={filteredSpecialProductsIds}
                                            getPrioritySpecialForProduct={getPrioritySpecialForProduct}
                                            getMostRecentSpecialId={getMostRecentSpecialId}
                                            isMenu={true}
                                            paginationLimit={totalUniqueItemsFetched}
                                            pricingType={cartData?.pricingType}
                                            showMultipleSpecials={menuConfig.showMultipleSpecials}
                                        />
                                    </ErrorBoundary>
                                ) : shouldShowNoProducts ? (
                                    // No products message
                                    <div className="headless-no-products">
                                        {selectedFilters.search?.length > 0 ? (
                                            <p>Sorry, no products were found! Please try refining your search, or use the filter options to narrow down the products.</p>
                                        ) : countActiveFilters(selectedFilters, menuFilter) > 0 ? (
                                            <>
                                                <p>Sorry! No products were found that matched your filters.</p>
                                            </>
                                        ) : (
                                            <p>Sorry! No products are currently available.</p>
                                        )}
                                    </div>
                                ) : selectedFilters.specials && !specialsExist && queryArgs && isFetchedMenuData ? (
                                    <p className="headless-no-products">Sorry, but there are no specials currently in stock. Check back soon!</p>
                                ) : null}
                                </div>
                            </>
                            );
                        })()}

                        {(totalUniqueItemsFetched > 0 || hasNextPageProductsData || isFetchingNextPageProductsData) && (
                            <ErrorBoundary
                                fallback={(props) =>
                                    <ErrorComponent
                                        {...props}
                                        componentName="Load more button"
                                        parentName="Menu"
                                    />
                                }
                            >
                                {isFetchingNextPageProductsData && <LoadingSpinner />}
                                {(!isFetchingNextPageProductsData &&
                                    (hasNextPageProductsData ||
                                        canDisplayMoreProducts(totalUniqueItemsFetched, displayFactorRef.current, paginationLimit))) && (
                                        <button
                                            ref={loadMoreButtonRef}
                                            className="headless-button headless-button-small headless-load-more"
                                            onClick={handleLoadMore}
                                        >
                                            Load More
                                        </button>
                                    )}
                            </ErrorBoundary>
                        )}
                    </div>
                </div>

                <div className="headless-shop-tags">
                    <h1>Cannabis Dispensary in {seoGeos ? seoGeos : (niceName || retailerName)} | {orderText}</h1>
                </div>

                {productPopupData && (
                    <SingleProduct
                        productPopupData={productPopupData}
                        setProductPopupData={setProductPopupData}
                        specialProductsIds={filteredSpecialProductsIds}
                    />
                )}
            </PriceRangeContext.Provider>
        </>
    );
};

// Memoize MenuContentInner to prevent re-renders from parent/context when props haven't changed
const MenuContent = React.memo(MenuContentInner, (prevProps, nextProps) => {
    // Only re-render if props actually changed
    return prevProps.rootId === nextProps.rootId &&
        prevProps.forcedOrderType === nextProps.forcedOrderType;
});

export default React.memo(Menu, (prevProps, nextProps) => {
    // Custom comparison - only re-render if props actually changed
    const isEqual = prevProps.rootId === nextProps.rootId &&
        prevProps.forcedOrderType === nextProps.forcedOrderType;
    return isEqual;
});
